import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, ChevronRightIcon } from '@navikt/aksel-icons';
import { ContainerCard } from '../ContainerCard/ContainerCard';
import { formatNorwegianDate } from '../../utils/dateFormatters';
import { formatNOK } from '../../utils/currencyFormatters';
import classes from './UpcomingFollowUps.module.css';

export type UpcomingFollowUp = {
  leadId: number;
  companyName: string;
  followUpDate: Date;
  daysUntil: number;
  estimatedMonthlyValueNOK: number;
};

export type UpcomingFollowUpsProps = {
  followUps: UpcomingFollowUp[];
};

export const UpcomingFollowUps = ({ followUps }: UpcomingFollowUpsProps): ReactElement => {
  const navigate = useNavigate();

  if (followUps.length === 0) {
    return (
      <ContainerCard>
        <ContainerCard.Title icon={<CalendarIcon />}>
          Oppkommende oppfølginger
        </ContainerCard.Title>
        <ContainerCard.Body>
          <div className={classes.emptyState}>
            <CalendarIcon className={classes.emptyIcon} />
            <p className={classes.emptyText}>Ingen oppfølginger planlagt de neste 7 dagene</p>
          </div>
        </ContainerCard.Body>
      </ContainerCard>
    );
  }

  return (
    <ContainerCard>
      <ContainerCard.Title icon={<CalendarIcon />}>
        Oppkommende oppfølginger ({followUps.length})
      </ContainerCard.Title>
      <ContainerCard.Body noPadding>
        <div className={classes.followUpList}>
          {followUps.map((followUp) => {
            const urgencyClass =
              followUp.daysUntil === 0 ? classes.today :
              followUp.daysUntil === 1 ? classes.tomorrow :
              followUp.daysUntil <= 3 ? classes.soon :
              classes.later;

            return (
              <div
                key={followUp.leadId}
                className={`${classes.followUpItem} ${urgencyClass}`}
                onClick={() => navigate(`/partner-admin/leads/${followUp.leadId}`)}
              >
                <div className={classes.followUpDate}>
                  <CalendarIcon className={classes.dateIcon} />
                  <div className={classes.dateInfo}>
                    <span className={classes.dateText}>
                      {formatNorwegianDate(followUp.followUpDate)}
                    </span>
                    <span className={classes.daysUntil}>
                      {followUp.daysUntil === 0
                        ? 'I dag'
                        : followUp.daysUntil === 1
                        ? 'I morgen'
                        : `Om ${followUp.daysUntil} dager`}
                    </span>
                  </div>
                </div>

                <div className={classes.followUpCompany}>
                  <span className={classes.companyName}>{followUp.companyName}</span>
                  <span className={classes.monthlyValue}>
                    {formatNOK(followUp.estimatedMonthlyValueNOK)}/mnd
                  </span>
                </div>

                <ChevronRightIcon className={classes.chevronIcon} />
              </div>
            );
          })}
        </div>
      </ContainerCard.Body>
    </ContainerCard>
  );
};
